
import { useState, useEffect, useRef } from 'react'
import { Modal } from '../base/Modal'
import { Button } from '../base/Button'
import { Input } from '../base/Input'
import { useMessages, Conversation } from '../../hooks/useMessages'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { mockDiscussions } from '../../mocks/discussions'

interface MessagesModalProps {
  isOpen: boolean
  onClose: () => void
  startConversationWith?: string
  recipientName?: string
}

interface RecipientInfo {
  id: string
  username: string
  avatar_url: string | null
}

export const MessagesModal = ({ isOpen, onClose, startConversationWith, recipientName }: MessagesModalProps) => {
  const { user } = useAuth()
  const {
    conversations,
    messages,
    currentConversation,
    setCurrentConversation,
    sendMessage,
    startConversation,
    loading,
    fetchConversations
  } = useMessages()
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [startingConversation, setStartingConversation] = useState(false)
  const [recipient, setRecipient] = useState<RecipientInfo | null>(null)
  const [showNewConversation, setShowNewConversation] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Fetch recipient info when starting a new conversation
  useEffect(() => {
    if (startConversationWith && isOpen && user) {
      fetchRecipientInfo(startConversationWith)
    }
  }, [startConversationWith, isOpen, user])

  const fetchRecipientInfo = async (userId: string) => {
    // First check mock users
    const mockUser = mockDiscussions.find(d => d.author.id === userId)
    if (mockUser) {
      setRecipient({
        id: mockUser.author.id,
        username: mockUser.author.username,
        avatar_url: mockUser.author.avatar
      })
      setShowNewConversation(true)
      return
    }

    // Fetch from Supabase
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, username, avatar_url')
        .eq('id', userId)
        .maybeSingle()

      if (!error && data) {
        setRecipient({
          id: data.id,
          username: data.username || 'Unknown User',
          avatar_url: data.avatar_url
        })
        setShowNewConversation(true)
      }
    } catch (err) {
      console.error('Error fetching recipient:', err)
    }
  }

  const handleStartConversation = async () => {
    if (!startConversationWith || !user || startingConversation) return

    setStartingConversation(true)
    try {
      const conversationId = await startConversation(startConversationWith)
      if (conversationId) {
        setCurrentConversation(conversationId)
        setShowNewConversation(false)
        // Refresh conversations list
        await fetchConversations()
      }
    } catch (error) {
      console.error('Error starting conversation:', error)
    } finally {
      setStartingConversation(false)
    }
  }

  const handleSendFirstMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || !startConversationWith || !user || sending) return

    setSending(true)
    try {
      // First create the conversation
      const conversationId = await startConversation(startConversationWith)
      if (conversationId) {
        // Then send the message
        await sendMessage(conversationId, newMessage)
        setNewMessage('')
        setCurrentConversation(conversationId)
        setShowNewConversation(false)
        await fetchConversations()
      }
    } catch (error) {
      console.error('Error sending first message:', error)
    } finally {
      setSending(false)
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || !currentConversation || sending) return

    setSending(true)
    try {
      await sendMessage(currentConversation, newMessage)
      setNewMessage('')
    } catch (error) {
      console.error('Error sending message:', error)
    } finally {
      setSending(false)
    }
  }

  const getOtherParticipant = (conversation: Conversation) => {
    return conversation.participants.find(p => p.user_id !== user?.id)
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (diffInHours < 168) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }
  }

  const safeCount = (count: any): number => {
    if (typeof count === 'number') return count
    if (count && typeof count === 'object' && 'count' in count) {
      return Number(count.count) || 0
    }
    return 0
  }

  const handleClose = () => {
    setShowNewConversation(false)
    setRecipient(null)
    setNewMessage('')
    onClose()
  }

  // Not logged in state
  if (!user) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Messages">
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
            <i className="ri-message-3-line text-2xl text-teal-600"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Sign in to message</h3>
          <p className="text-gray-500 text-center text-sm mb-6">
            You need to be signed in to send direct messages to other users.
          </p>
          <Button onClick={handleClose} className="whitespace-nowrap">
            Close
          </Button>
        </div>
      </Modal>
    )
  }

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Messages">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Messages" size="large">
      <div className="flex h-[480px]">
        {/* Conversations List */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Conversations</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {/* New conversation item when starting fresh */}
            {showNewConversation && recipient && (
              <div
                onClick={() => {
                  setCurrentConversation(null)
                  setShowNewConversation(true)
                }}
                className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                  !currentConversation && showNewConversation ? 'bg-teal-50 border-teal-200' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                    {recipient.avatar_url ? (
                      <img src={recipient.avatar_url} alt={recipient.username} className="w-full h-full object-cover object-top" />
                    ) : (
                      recipient.username[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {recipient.username}
                      </p>
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">New</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">Start a conversation</p>
                  </div>
                </div>
              </div>
            )}

            {conversations.length === 0 && !showNewConversation ? (
              <div className="p-4 text-center text-gray-500">
                <i className="ri-message-3-line text-2xl mb-2 block"></i>
                <p className="text-sm">No conversations yet</p>
              </div>
            ) : (
              conversations.map((conversation) => {
                const otherParticipant = getOtherParticipant(conversation)
                const unreadCount = safeCount(conversation.unread_count)
                return (
                  <div
                    key={conversation.id}
                    onClick={() => {
                      setCurrentConversation(conversation.id)
                      setShowNewConversation(false)
                    }}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      currentConversation === conversation.id ? 'bg-teal-50 border-teal-200' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {otherParticipant?.user_profile?.username?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {otherParticipant?.user_profile?.username || 'Unknown User'}
                          </p>
                          {unreadCount > 0 && (
                            <span className="bg-teal-600 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                              {unreadCount}
                            </span>
                          )}
                        </div>
                        {conversation.last_message && (
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500 truncate">
                              {conversation.last_message.content}
                            </p>
                            <p className="text-xs text-gray-400 ml-2 flex-shrink-0">
                              {formatTime(conversation.last_message.created_at)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 flex flex-col">
          {/* New conversation view */}
          {showNewConversation && recipient && !currentConversation ? (
            <>
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-emerald-50">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                    {recipient.avatar_url ? (
                      <img src={recipient.avatar_url} alt={recipient.username} className="w-full h-full object-cover object-top" />
                    ) : (
                      recipient.username[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{recipient.username}</h3>
                    <p className="text-xs text-teal-600">New conversation</p>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white text-2xl font-semibold overflow-hidden">
                    {recipient.avatar_url ? (
                      <img src={recipient.avatar_url} alt={recipient.username} className="w-full h-full object-cover object-top" />
                    ) : (
                      recipient.username[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{recipient.username}</h3>
                  <p className="text-gray-500 text-sm mb-4">Send a message to start the conversation</p>
                </div>
              </div>

              <form onSubmit={handleSendFirstMessage} className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex space-x-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Message ${recipient.username}...`}
                    className="flex-1"
                    disabled={sending}
                  />
                  <Button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="whitespace-nowrap"
                  >
                    {sending ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-1"></i>
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : currentConversation ? (
            <>
              {/* Messages Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {getOtherParticipant(conversations.find(c => c.id === currentConversation)!)?.user_profile?.username?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    {getOtherParticipant(conversations.find(c => c.id === currentConversation)!)?.user_profile?.username || 'Unknown User'}
                  </h3>
                </div>
              </div>

              {/* Messages List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <i className="ri-chat-smile-3-line text-4xl mb-2 block"></i>
                      <p className="text-sm">No messages yet. Say hello!</p>
                    </div>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                        message.sender_id === user?.id
                          ? 'bg-teal-600 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`}>
                        <p className="text-sm">{message.content}</p>
                        <p className={`text-xs mt-1 ${
                          message.sender_id === user?.id ? 'text-teal-100' : 'text-gray-500'
                        }`}>
                          {formatTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    disabled={sending}
                  />
                  <Button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="whitespace-nowrap"
                  >
                    {sending ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-send-plane-line"></i>
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <i className="ri-message-3-line text-4xl mb-4 block"></i>
                <p className="font-medium">Select a conversation</p>
                <p className="text-sm mt-1">Choose from the sidebar to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
